import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { listChildren, Child, getChildStats, ChildStats, askParentCoach, getCoachHistory, CoachMessage } from '../services/parentApi';

const SUGGESTIONS = [
  'Comment va mon enfant ?',
  'Ses progrès cette semaine ?',
  'Comment l\'aider ?',
  'Comment fonctionne Methodix ?',
  'Le Premium vaut le coup ?',
];

export default function Dashboard() {
  const [children, setChildren] = useState<Child[]>([]);
  const [childStats, setChildStats] = useState<ChildStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Coach state
  const [activeChildId, setActiveChildId] = useState<string | null>(null);
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState('');
  const [remaining, setRemaining] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadChildren();
  }, []);

  useEffect(() => {
    if (children.length > 0 && !activeChildId) {
      selectChild(children[0].student_id);
    }
  }, [children]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadChildren() {
    try {
      const data = await listChildren();
      setChildren(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function selectChild(childId: string) {
    setActiveChildId(childId);
    setMessages([]);
    setChatError('');
    // Charger stats + historique en parallèle
    const [history] = await Promise.all([
      getCoachHistory(childId).catch(() => [] as CoachMessage[]),
      getChildStats(childId).then(setChildStats).catch(() => {}),
    ]);
    setMessages(history);
  }

  async function handleSend(text?: string) {
    const message = text || input.trim();
    if (!message || !activeChildId || chatLoading) return;

    setInput('');
    setChatError('');

    const userMsg: CoachMessage = { role: 'user', content: message, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setChatLoading(true);

    try {
      const result = await askParentCoach(activeChildId, message);
      const assistantMsg: CoachMessage = {
        role: 'assistant',
        content: result.response,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMsg]);
      setRemaining(result.remaining_messages);
    } catch (err: any) {
      setChatError(err.message);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setChatLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Espace Parent</h1>
        </div>
        {error && <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">{error}</div>}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <span className="text-6xl">👨‍👩‍👧</span>
          <h2 className="mt-4 text-xl font-semibold text-slate-900">Aucun enfant lié</h2>
          <p className="mt-2 text-slate-600">
            Demandez le code parent à votre enfant depuis son application Methodix
          </p>
          <Link
            to="/link"
            className="mt-6 inline-block px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            Ajouter un enfant
          </Link>
        </div>
      </div>
    );
  }

  const activeChild = children.find(c => c.student_id === activeChildId);

  return (
    <div className="flex gap-6 h-[calc(100vh-8rem)]">
      {/* Colonne gauche : enfants + stats */}
      <div className="w-1/2 flex flex-col overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-slate-900">Mes enfants</h1>
          <Link
            to="/link"
            className="text-sm px-3 py-1.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            + Ajouter
          </Link>
        </div>

        {/* Bandeau promo Premium */}
        {activeChild && !activeChild.is_premium && (
          <div className="mb-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-3xl">🚀</span>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-amber-900">
                  Offrez à {activeChild.name} un coach IA illimité
                </h3>
                <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                  Avec Premium, {activeChild.name} peut poser toutes ses questions, créer des fiches de révision et se préparer aux contrôles sans restriction. Un vrai coup de boost pour ses résultats !
                </p>
                <div className="flex items-center gap-3 mt-3">
                  <Link
                    to={`/upgrade/${activeChildId}`}
                    className="inline-flex items-center px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-lg hover:bg-amber-600 transition-colors shadow-sm"
                  >
                    ⭐ Découvrir Premium
                  </Link>
                  <span className="text-xs text-amber-600">À partir de 4,99€/mois</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sélecteur enfants si plusieurs */}
        {children.length > 1 && (
          <div className="flex gap-2 mb-4">
            {children.map(child => (
              <button
                key={child.student_id}
                onClick={() => selectChild(child.student_id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  child.student_id === activeChildId
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {child.name}
              </button>
            ))}
          </div>
        )}

        {/* Carte unique enfant : profil + stats + crédits */}
        {activeChild && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
            {/* Profil */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">👤</span>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{activeChild.name}</h2>
                  {activeChild.is_premium ? (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                      ⭐ Premium
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                      Gratuit
                    </span>
                  )}
                </div>
              </div>
              {!activeChild.is_premium && (
                <Link
                  to={`/upgrade/${activeChildId}`}
                  className="px-3 py-1.5 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors"
                >
                  ⭐ Premium
                </Link>
              )}
            </div>

            {!activeChild.consent_given && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800 mb-2">⚠️ Consentement parental requis</p>
                <Link
                  to={`/child/${activeChildId}`}
                  className="text-sm text-amber-700 font-medium underline"
                >
                  Donner mon consentement →
                </Link>
              </div>
            )}

            {/* Stats */}
            {childStats && (
              <>
                <div className="border-t border-slate-100 pt-4 mb-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Activité</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <div className="text-xl font-bold text-primary-600">{childStats.stats.ai_interactions_last_7_days}</div>
                      <div className="text-xs text-slate-500">🤖 Coach (7j)</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <div className="text-xl font-bold text-primary-600">{childStats.stats.ai_interactions_total}</div>
                      <div className="text-xs text-slate-500">🤖 Coach (total)</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <div className="text-xl font-bold text-primary-600">{childStats.stats.course_sheets_count}</div>
                      <div className="text-xs text-slate-500">📝 Fiches</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <div className="text-xl font-bold text-primary-600">{childStats.stats.quiz_count}</div>
                      <div className="text-xs text-slate-500">❓ Quiz</div>
                    </div>
                  </div>
                </div>

                {/* Crédits */}
                <div className="border-t border-slate-100 pt-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Crédits</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xl font-bold text-slate-900">{childStats.stats.credits_remaining}</span>
                      <span className="text-xs text-slate-500 ml-1">restants</span>
                    </div>
                    <div className="w-20 h-2.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 rounded-full"
                        style={{
                          width: `${Math.min(100, (childStats.stats.credits_remaining / (childStats.stats.is_premium ? 1000 : 100)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-slate-400">
                    {childStats.stats.is_premium ? 'Premium : 1000/mois' : 'Gratuit : 100/mois'}
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Colonne droite : Coach */}
      <div className="w-1/2 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">🤖 Coach Methodix</h2>
          {remaining !== null && (
            <span className="text-xs text-slate-400">
              {remaining} msg restant{remaining !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3">
          {messages.length === 0 && !chatLoading && (
            <div className="text-center py-6">
              <div className="text-4xl mb-3">🎓</div>
              <p className="text-sm text-slate-500 max-w-xs mx-auto">
                Posez vos questions sur les progrès de {activeChild?.name || 'votre enfant'} ou le fonctionnement de Methodix.
              </p>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-3 py-2 ${
                  msg.role === 'user'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-800'
                }`}
              >
                {msg.role === 'assistant' && (
                  <div className="text-xs text-slate-400 mb-0.5">🤖 Coach</div>
                )}
                <div className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</div>
              </div>
            </div>
          ))}

          {chatLoading && (
            <div className="flex justify-start">
              <div className="bg-white border border-slate-200 rounded-2xl px-3 py-2">
                <div className="flex space-x-1.5">
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Error */}
        {chatError && (
          <div className="mt-2 p-2 bg-red-50 text-red-600 text-xs rounded-lg">{chatError}</div>
        )}

        {/* Suggestions */}
        {!chatLoading && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {SUGGESTIONS.map((suggestion, i) => (
              <button
                key={i}
                onClick={() => handleSend(suggestion)}
                className="px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs text-slate-600 hover:bg-primary-50 hover:border-primary-300 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="mt-2 flex space-x-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Question sur ${activeChild?.name || 'votre enfant'}...`}
            disabled={chatLoading}
            className="flex-1 px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
          />
          <button
            onClick={() => handleSend()}
            disabled={chatLoading || !input.trim()}
            className="px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}
